import React, { useState } from 'react';
import { Folder, FolderOpen, ChevronDown, ChevronRight } from 'lucide-react';
import type { AssetMetadata } from '@linein/shared';

interface FolderNode {
  _name: string;
  children: Record<string, FolderNode>;
}

export const getFolderTree = (assets: AssetMetadata[], extraFolders: string[]) => {
  const tree: Record<string, FolderNode> = {};

  // Folders derived from assets' folderPath
  assets.forEach((asset) => {
    // Use folderPath instead of sourcePath
    if (!asset.folderPath) return;
    
    const pathParts = asset.folderPath.split('/').filter(p => p);

    let current: any = tree;
    pathParts.forEach((part) => {
      if (!part) return;
      if (!current[part]) current[part] = { _name: part, children: {} };
      current = current[part].children;
    });
  });

  // Folders manually added
  extraFolders.forEach((path) => {
    const parts = path.split('/').filter((p) => p);
    let current: any = tree;
    parts.forEach((part) => {
      if (!current[part]) current[part] = { _name: part, children: {} };
      current = current[part].children;
    });
  });

  return tree;
};

interface FolderTreeItemProps {
  name: string;
  path: string;
  children: Record<string, FolderNode>;
  onSelect: (path: string) => void;
  onDrop: (assetId: string, folderPath: string) => void;
  onContextMenu: (e: React.MouseEvent, path: string) => void;
  currentPath: string;
}

export const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  name,
  path,
  children,
  onSelect,
  onDrop,
  onContextMenu,
  currentPath,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const fullPath = path ? `${path}/${name}` : name;
  const hasChildren = Object.keys(children).length > 0;
  const isSelected = currentPath === fullPath;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const assetId = e.dataTransfer.getData('assetId');
    if (assetId) onDrop(assetId, fullPath);
  };

  return (
    <div className="ml-2">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={(e) => onContextMenu(e, fullPath)}
        className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer border-2 border-transparent ${
          isSelected
            ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
            : 'text-slate-500 hover:bg-slate-50'
        } ${isDragOver ? 'border-dashed border-indigo-400 bg-indigo-50' : ''}`}
        onClick={() => {
          onSelect(fullPath);
          if (hasChildren) setIsOpen(!isOpen);
        }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          >
            {isOpen ? (
              <ChevronDown size={12} className="shrink-0" />
            ) : (
              <ChevronRight size={12} className="shrink-0" />
            )}
          </button>
        ) : (
          <div className="w-3" />
        )}
        {isOpen ? (
          <FolderOpen size={14} className="shrink-0 text-indigo-400" />
        ) : (
          <Folder size={14} className="shrink-0 text-slate-300" />
        )}
        <span className="truncate flex-1">{name}</span>
      </div>
      {isOpen && hasChildren && (
        <div className="ml-2 border-l border-slate-100 mt-1">
          {Object.keys(children).map((childKey) => (
            <FolderTreeItem
              key={childKey}
              name={childKey}
              path={fullPath}
              children={children[childKey].children}
              onSelect={onSelect}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
};
